import { z } from 'zod';

import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage';

import type { ProductFeedbackType } from '@littlebird/ui-utils';

const STORAGE_KEY = 'feedbackDraftEdits';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const FeedbackDraftEditSchema = z.object({
  text: z.string().optional(),
  feedbackType: z.enum(['bug', 'feature', 'other']).optional(),
  updatedAt: z.number(),
});

const FeedbackDraftEditsSchema = z.record(z.string(), FeedbackDraftEditSchema);

type FeedbackDraftEdits = z.infer<typeof FeedbackDraftEditsSchema>;

export interface FeedbackDraftEdit {
  text?: string;
  feedbackType?: ProductFeedbackType;
}

function readAll(): FeedbackDraftEdits {
  const parsed = FeedbackDraftEditsSchema.safeParse(
    getLocalStorageItem(STORAGE_KEY)
  );
  return parsed.success ? parsed.data : {};
}

function writeAll(edits: FeedbackDraftEdits): void {
  try {
    setLocalStorageItem(STORAGE_KEY, edits);
  } catch (error) {
    console.error('Failed to save feedback draft edits:', error);
  }
}

export function readFeedbackDraftEdit(
  draftId: string
): FeedbackDraftEdit | undefined {
  return readAll()[draftId];
}

export function saveFeedbackDraftEdit(
  draftId: string,
  edit: FeedbackDraftEdit
): void {
  const now = Date.now();
  const edits = Object.fromEntries(
    Object.entries(readAll()).filter(
      ([id, saved]) => id === draftId || now - saved.updatedAt < MAX_AGE_MS
    )
  );
  edits[draftId] = { ...edits[draftId], ...edit, updatedAt: now };
  writeAll(edits);
}

export function clearFeedbackDraftEdit(draftId: string): void {
  const edits = readAll();
  if (!(draftId in edits)) return;
  delete edits[draftId];
  writeAll(edits);
}
